import React, { useEffect, createRef, PropsWithChildren, MouseEvent } from 'react';
import ReactDOM from 'react-dom';
import useClickOutside from '../hooks/useClickOutside';
import CloseButton from './CloseButton';

interface Props {
  isOpen: boolean;
  onClose: (event?: MouseEvent) => unknown;
  scrollPosition: number;
  showCloseButton?: boolean;
  style?: object;
  className?: string;
}

const Modal = ({
  isOpen,
  onClose,
  children,
  scrollPosition,
  showCloseButton = true,
  className,
  ...props
}: PropsWithChildren<Props>) => {
  const ref = createRef<HTMLDivElement>();

  useEffect(() => {
    if (isOpen) {
      document?.querySelector('#__next')?.classList.add('hidden');
      document.body.classList.remove('animate-fadeIn');
      window.scroll(0, 0);
    } else {
      document?.querySelector('#__next')?.classList.remove('hidden');
      document.body.classList.add('animate-fadeIn');
      window.scroll(0, scrollPosition);
    }
    return () => document?.querySelector('#__next')?.classList.remove('hidden');
  }, [isOpen]);

  useClickOutside(ref, () => onClose());

  // add modal to root of DOM to prevent z-index collisions
  const rootElemRef = React.useRef<HTMLElement | null>(null);
  useEffect(() => {
    rootElemRef.current = document.body;

    return function removeElement() {
      rootElemRef.current = null;
    };
  }, []);

  if (!rootElemRef.current) {
    return null;
  }

  return ReactDOM.createPortal(
    <div {...props} className={`${className ? className : ''}`}>
      {isOpen && (
        <>
          {showCloseButton && <CloseButton onClose={() => onClose()} fixed />}

          <div className="flex items-center justify-center">
            <div ref={ref}>{children}</div>
          </div>
        </>
      )}
    </div>,
    rootElemRef.current
  );
};

Modal.displayName = 'Modal';

export default Modal;
